import React from 'react';
import classNames from 'classnames';
import Resizable from 'ui/resizable';

import Store, { ProfileStore } from 'store';

import './ProfileList.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faMinus, faPencilAlt, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { faCheckSquare, faSquare } from '@fortawesome/free-regular-svg-icons';
import EditingContext from 'store/editingContext';
import Locale from 'data/locale';
import { OverlayContext } from 'ui/utils/OverlayManager';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import scrollIntoView from 'scroll-into-view-if-needed';
import Accordion from 'ui/accordion';
import { chainedRef } from 'utils';
import { MaxProfileSetName, MaxProfileSets } from 'common';
import { stateRemoveProfile } from 'logic/stateReducers';

const _L = Locale("ui-profiles");

function TextEdit({ value, onChange }) {
  const ref = React.useRef();
  React.useEffect(() => {
    scrollIntoView(ref.current, {block: "center", inline: "center"});
    ref.current.select();
    ref.current.focus();
  }, []);
  const onBlur = React.useCallback(() => {
    onChange(ref.current.value);
  }, [onChange]);
  const onKeyDown = React.useCallback(e => {
    switch (e.key) {
    case "Enter":
      ref.current.blur();
      break;
    case "Esc":
    case "Escape":
      onChange(null);
      break;
    // no default
    }
  }, [onChange]);
  return <input defaultValue={value} ref={ref} onBlur={onBlur} onKeyDown={onKeyDown} maxLength={MaxProfileSetName}/>;
}

function ProfileLine({ profile, children }) {
  return (
    <React.Fragment>
      <span className={`d2p-class-icon d2p-icon-large d2p-round d2p-class-${profile.class} d2p-${profile.gender}`}/>
      <span className="d2p-profile-info">
        <span className="d2p-label">{children}</span>
      </span>
    </React.Fragment>
  );
}

const ProfileItem = React.memo(function ProfileItem({ index, profile, active, highlight, setFocused, deleting, setDeleting }) {
  const dispatch = Store.useDispatch();
  const [editing, setEditing] = React.useState(false);

  const onClick = React.useCallback(e => {
    if (e.target.tagName === "INPUT") return;
    // eslint-disable-next-line no-unused-expressions
    document.activeElement?.blur();
    dispatch.setActiveProfile(profile.id); 
  }, [dispatch, profile.id]);

  const onEdit = React.useCallback(e => {
    e.stopPropagation();
    setEditing(flag => !flag);
  }, []);
  const onEditChange = React.useCallback(name => {
    setEditing(false);
    if (name) {
      dispatch.renameProfile(profile.id, name.substring(0, MaxProfileSetName));
    }
  }, [dispatch, profile.id]);

  const onEditSignal = React.useCallback(({ type, id }) => {
    if (type === "profile" && id === profile.id) {
      setEditing(true);
      return true;
    }
  }, [profile.id]);
  EditingContext.useHandler(onEditSignal);

  const labelRef = React.useRef();
  React.useEffect(() => {
    if (active) {
      scrollIntoView(labelRef.current, {scrollMode: "if-needed", block: "center", boundary: labelRef.current.parentNode});
    }
  }, [active]);

  const onMouseOver = React.useCallback(() => setFocused(profile.id), [setFocused, profile.id]);
  const onMouseOut = React.useCallback(() => setFocused(null), [setFocused]);

  const onDeleteClick = React.useCallback(e => {
    e.stopPropagation();
    setDeleting(del => ({...del, [profile.id]: !del[profile.id]}));
  }, [setDeleting, profile.id]);

  return (
    <Draggable draggableId={profile.id.toString()} index={index}>
      {provided => (
        <li className={classNames("d2p-Profile", {"d2p-active": active, "d2p-highlight": highlight || editing})}
            onClick={onClick}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={chainedRef(labelRef, provided.innerRef)}>
          <ProfileLine profile={profile}>
            {editing ? <TextEdit value={profile.name} onChange={onEditChange}/> : profile.name}
          </ProfileLine>
          <span className="d2p-buttons">
            {deleting ? (
              <span className="d2p-button d2p-removing" onClick={onDeleteClick}>
                <FontAwesomeIcon icon={deleting[profile.id] ? faCheckSquare : faSquare}/>
              </span>
            ) : (
              <span className="d2p-button d2p-rename" onClick={onEdit}>
                <FontAwesomeIcon icon={faPencilAlt}/>
              </span>
            )}
          </span>
        </li>
      )}
    </Draggable>
  );
});

function ProfileDropList({ expanded, deleting, setDeleting }) {
  const [height, setHeight] = React.useState(null);
  const [focused, setFocused] = React.useState();
  const dispatch = Store.useDispatch();

  const profiles = Store.useSelector(state => state.profiles);
  const activeId = Store.useProfile(profile => profile.id);

  const onDragEnd = React.useCallback(({ destination, source }) => {
    if (!destination || source.index === destination.index) return;
    dispatch.moveProfile(source.index, destination.index);
  }, [dispatch]);

  const highlight = focused || activeId;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="d2p-ProfileDropList">
        <Accordion visible={expanded}>
          <Resizable className="d2p-profiles" side={expanded ? "n" : ""} height={height} setHeight={setHeight}>
            <Droppable droppableId="profile-list">
              {provided => (
                <ul {...provided.droppableProps} ref={provided.innerRef}>
                  {profiles.map((profile, index) => (
                    <ProfileItem key={profile.id}
                                 index={index}
                                 profile={profile}
                                 active={profile.id === activeId}
                                 highlight={profile.id === highlight}
                                 setFocused={setFocused}
                                 deleting={deleting}
                                 setDeleting={setDeleting}/>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </Resizable>
        </Accordion>
      </div>
    </DragDropContext>
  );
}

function CreateProfileButton() {
  const overlays = React.useContext(OverlayContext);
  const dispatch = Store.useDispatch();
  const editingDispatch = EditingContext.useDispatch();
  const maxSets = Store.useSelector(state => state.profiles.length >= MaxProfileSets);
  const onAdd = React.useCallback(async e => {
    const result = await overlays.menu(e, [_L`Blank set`, _L`Clone current set`]);
    if (result == null) return;

    const id = Store.newId();
    dispatch.addProfile(state => {
      if (state.profiles.length >= MaxProfileSets) return null;
      const profile = state.profiles[state.activeProfile];
      if (result === 0) {
        return {
          ...ProfileStore.initialState,
          name: _L`Set ${state.profiles.length + 1}`,
          class: profile.class,
          gender: profile.gender,
          seasonal: profile.seasonal,
          id
        };
      } else {
        return {...profile, name: _L`Set ${state.profiles.length + 1}`, id};
      }
    });
    editingDispatch({type: "profile", id});
  }, [overlays, dispatch, editingDispatch]);

  return (
    <span className={classNames("d2p-button d2p-add", {"d2p-disabled": maxSets})} onClick={maxSets ? null : onAdd}>
      <FontAwesomeIcon icon={faPlus}/>
    </span>
  );
}

function ProfileDropdown({ deleteMode }) {
  const [expanded, setExpanded] = React.useState(false);
  const [deleting, setDeleting] = React.useState(null);
  const onToggle = React.useCallback(() => {
    setExpanded(state => !state);
    setDeleting(null);
  }, []);

  const dispatch = Store.useDispatch();
  const profile = Store.useProfile();
  const numProfiles = Store.useSelector(state => state.profiles.length);

  const onDeleteClick = React.useCallback(() => {
    if (deleting) {
      dispatch.setState(state => stateRemoveProfile(state, ...Object.keys(deleting).filter(id => deleting[id]).map(id => parseInt(id))));
      setDeleting(null);
    } else {
      setDeleting({});
    }
  }, [deleting, dispatch]);

  return (
    <div className={classNames("d2p-ProfileDropdown", {"d2p-expanded": expanded})}>
      <div className="d2p-Profile d2p-header">
        <ProfileLine profile={profile}>{profile.name}</ProfileLine>
        <span className="d2p-buttons">
          {!expanded && numProfiles > 1 && <span className="d2p-count" onClick={onToggle}>
            ({numProfiles})
          </span>}
          {!deleting && <CreateProfileButton/>}
          {expanded && <span className={classNames("d2p-button d2p-remove")} onClick={onDeleteClick}>
            <FontAwesomeIcon icon={deleting ? faTrashAlt : faMinus}/>
          </span>}
          <span className="d2p-button d2p-toggle" onClick={onToggle}>
            <FontAwesomeIcon icon={faChevronUp}/>
          </span>
        </span>
      </div>
      <ProfileDropList deleting={deleting} setDeleting={setDeleting} expanded={expanded}/>
    </div>
  );
}

export default React.memo(function ProfileList() {
  return (
    <div className="d2p-ProfileList">
      <ProfileDropdown deleteMode={false}/>
    </div>
  );
});
